import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  Lock,
  Pen,
  RefreshCw,
  Search,
  Shield,
  UserPlus,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { useFeaturePermissions } from '../../../components/globalfeatures';
import QueueConfidentialityLevelControl, {
  FEATURE_UPDATE_TICKET_CONFIDENTIALITY_LEVEL,
} from '../../../components/tickets/QueueConfidentialityLevelControl';
import InquiryAssignModal from '../../../components/tickets/InquiryAssignModal';
import { ticketService } from '../../../api/ticketService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { useStatuses } from '../../../hooks/useStatuses';

function confidentialityBadge(level) {
  const normalized = String(level || 'NORMAL').toUpperCase();
  if (normalized === 'CONFIDENTIAL') {
    return (
      <Badge variant="destructive" className="gap-1">
        <Shield className="h-3 w-3" />
        Confidential
      </Badge>
    );
  }
  if (normalized === 'RESTRICTED') {
    return (
      <Badge className="gap-1 bg-amber-600 hover:bg-amber-700">
        <Lock className="h-3 w-3" />
        Restricted
      </Badge>
    );
  }
  return <Badge variant="secondary">{normalized}</Badge>;
}

/** Matches backend: level change only when assignee is null (queue tickets). */
function isUnassignedTicket(ticket) {
  const assigneeId = ticket?.assigneeId ?? ticket?.assignee_id;
  return assigneeId == null || assigneeId === '';
}

function isInquiryTicketRow(ticket) {
  return !!(ticket?.inquiryTypeId || ticket?.inquiryCategoryId);
}

const RestrictedConfidentialTickets = () => {
  const navigate = useNavigate();
  const viewPerms = useFeaturePermissions(FEATURE_UPDATE_TICKET_CONFIDENTIALITY_LEVEL);
  const { data: statuses = [] } = useStatuses();

  const [stats, setStats] = useState({ total: 0, restricted: 0, confidential: 0 });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pagination, setPagination] = useState(null);
  const [levelEditTicket, setLevelEditTicket] = useState(null);
  const [assignModalTicket, setAssignModalTicket] = useState(null);
  const [canStartInquiryAssignment, setCanStartInquiryAssignment] = useState(false);
  const pageSize = 10;

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await ticketService.getRestrictedConfidentialTicketStats();
      const body = response?.data;
      if (body?.success && body.data) {
        setStats({
          total: body.data.total ?? 0,
          restricted: body.data.restricted ?? 0,
          confidential: body.data.confidential ?? 0,
        });
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to load statistics'));
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: pageSize };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;
      if (levelFilter) params.confidentialityLevel = levelFilter;

      const response = await ticketService.getRestrictedConfidentialTickets(params);
      const body = response?.data;
      if (!body?.success) {
        throw new Error(body?.message || 'Failed to load tickets');
      }
      const pageData = body.data || {};
      setTickets(Array.isArray(pageData.content) ? pageData.content : []);
      setPagination({
        totalElements: pageData.totalElements ?? 0,
        totalPages: pageData.totalPages ?? 0,
        first: pageData.first,
        last: pageData.last,
      });
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to load Restricted / Confidential tickets'));
      setTickets([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter, levelFilter]);

  useEffect(() => {
    if (!viewPerms.canView) return;
    loadStats();
  }, [viewPerms.canView, loadStats]);

  useEffect(() => {
    if (!viewPerms.canView) return;
    loadTickets();
  }, [viewPerms.canView, loadTickets]);

  useEffect(() => {
    if (!viewPerms.canView) {
      setCanStartInquiryAssignment(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await ticketService.getPendingTeamLeadAssignments({ page: 0, size: 1 });
        const body = response?.data;
        const allowed = body && body.success !== false && body.success !== 'false';
        if (!cancelled) setCanStartInquiryAssignment(Boolean(allowed));
      } catch {
        if (!cancelled) setCanStartInquiryAssignment(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewPerms.canView]);

  const refreshListAndStats = useCallback(() => {
    loadStats();
    loadTickets();
  }, [loadStats, loadTickets]);

  const showAssignForTicket = (ticket) =>
    canStartInquiryAssignment && isInquiryTicketRow(ticket) && isUnassignedTicket(ticket);

  if (!viewPerms.canView) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6 flex items-center gap-3 text-amber-800">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p>You do not have permission to view Restricted or Confidential inquiries.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadTickets();
  };

  const ticketIdForNav = (ticket) => ticket.ticketId ?? ticket.id;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Restricted / Confidential Tickets</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Tenant-wide Restricted and Confidential inquiries (assigned and unassigned).
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total R/C</CardDescription>
            <CardTitle className="text-3xl">{statsLoading ? '…' : stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Restricted</CardDescription>
            <CardTitle className="text-3xl">{statsLoading ? '…' : stats.restricted}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Confidential</CardDescription>
            <CardTitle className="text-3xl">{statsLoading ? '…' : stats.confidential}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <CardTitle className="text-lg">Inquiries</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              loadStats();
              loadTickets();
            }}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground mb-1 block">Search (number or subject)</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Ticket number or subject"
                />
              </div>
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs text-muted-foreground mb-1 block">Status</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">All statuses</option>
                {statuses.map((s) => {
                  const code = s.statusCode || s.code;
                  if (!code) return null;
                  return (
                    <option key={code} value={code}>
                      {s.statusName || s.name || code}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs text-muted-foreground mb-1 block">Confidentiality</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={levelFilter}
                onChange={(e) => {
                  setLevelFilter(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">Restricted + Confidential</option>
                <option value="RESTRICTED">Restricted only</option>
                <option value="CONFIDENTIAL">Confidential only</option>
              </select>
            </div>
            <Button type="submit">Apply</Button>
          </form>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Ticket</th>
                  <th className="text-left p-3 font-medium">Subject</th>
                  <th className="text-left p-3 font-medium">Level</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Assignee</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && tickets.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      No Restricted or Confidential tickets match your filters.
                    </td>
                  </tr>
                )}
                {!loading
                  && tickets.map((ticket) => (
                    <tr key={ticketIdForNav(ticket)} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-mono text-xs">{ticket.ticketNumber ?? ticket.id}</td>
                      <td className="p-3 max-w-md truncate">{ticket.subject}</td>
                      <td className="p-3">{confidentialityBadge(ticket.confidentialityLevel)}</td>
                      <td className="p-3">{ticket.statusName || ticket.status || '—'}</td>
                      <td className="p-3">
                        {ticket.assigneeFullName
                          || ticket.assigneeName
                          || ticket.assignedAgent
                          || (ticket.assigneeId ? '—' : 'Unassigned')}
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center justify-end gap-1">
                          {showAssignForTicket(ticket) && (
                            <Button
                              type="button"
                              variant="default"
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => setAssignModalTicket(ticket)}
                            >
                              <UserPlus className="h-4 w-4 mr-1" />
                              Assign
                            </Button>
                          )}
                          {viewPerms.canUpdate && isUnassignedTicket(ticket) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setLevelEditTicket(ticket)}
                            >
                              <Pen className="h-4 w-4 mr-1" />
                              Update
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/agent/tickets/${ticketIdForNav(ticket)}`)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                Page {page + 1} of {pagination.totalPages} ({pagination.totalElements} tickets)
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 0 || loading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pagination.last || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <InquiryAssignModal
        ticket={assignModalTicket}
        isOpen={assignModalTicket != null}
        onClose={() => setAssignModalTicket(null)}
        onAssigned={refreshListAndStats}
      />

      {levelEditTicket && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setLevelEditTicket(null)}
        >
          <div
            className="bg-background rounded-lg border shadow-lg max-w-md w-full p-6 space-y-4"
            role="dialog"
            aria-labelledby="rc-level-update-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 id="rc-level-update-title" className="text-lg font-semibold">
                Update confidentiality
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {levelEditTicket.ticketNumber ?? ticketIdForNav(levelEditTicket)}
                {' — '}
                {levelEditTicket.subject}
              </p>
            </div>
            <QueueConfidentialityLevelControl
              ticketId={ticketIdForNav(levelEditTicket)}
              initialLevel={levelEditTicket.confidentialityLevel}
              onSaved={() => {
                // Uses leftRestrictedConfidentialList from control when saved === NORMAL (list refresh drops row).
                setLevelEditTicket(null);
                loadStats();
                loadTickets();
              }}
            />
            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={() => setLevelEditTicket(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestrictedConfidentialTickets;
